<template>
  <div class="ma-2">
    <v-card class="pa-3" outlined>
      <v-row class="ma-0" no-gutters>
        <v-col cols="12" sm="4">
          <div class="main-toolbar mb-4">
            <div class="icon-back">
              <v-img
                src="../../../assets/figmaIcons/white/reports.svg"
                max-width="55%"
              ></v-img>
            </div>

            <span class="main-toolbar-title">Relatórios - Dashboard</span>
          </div>
        </v-col>
        <v-col cols="12" sm="8">
          <div>
            <strong class="ml-2">Filtros:</strong>
            <v-row class="ma-0" dense align="center">
              <!-- Master/Admin filter (Master/Admin only) -->
              <v-col v-if="isMultiInstitution && isAdminOrMaster" cols="12" sm="2">
                <v-autocomplete
                  v-model="selectedAdminUserId"
                  :items="adminUsers"
                  item-text="displayName"
                  item-value="id"
                  :loading="adminUsersLoading"
                  clearable
                  outlined
                  class="primary-input"
                  :label="$t('Master / Admin')"
                  hide-details
                  append-icon="mdi-chevron-down"
                  no-data-text="Nenhum registro"
                  @change="onAdminUserChange"
                ></v-autocomplete>
                <div v-if="selectedAdminUserId" class="d-flex mt-1" style="gap: 4px">
                  <v-btn x-small depressed color="#bbcf20" style="color: #0f3565; font-weight: 600"
                    class="flex-grow-1" @click="savePreset" :loading="savingPreset">
                    <v-icon x-small left>mdi-content-save</v-icon>
                    {{ selectedAdminHasPreset ? 'Atualizar' : 'Salvar' }}
                  </v-btn>
                  <v-btn v-if="selectedAdminHasPreset" x-small depressed color="error"
                    class="flex-grow-1" @click="removePreset" :loading="savingPreset">
                    <v-icon x-small left>mdi-delete</v-icon> Limpar
                  </v-btn>
                </div>
              </v-col>

              <!-- Supervisor filter (Master/Admin only) -->
              <v-col v-if="isMultiInstitution && isAdminOrMaster" cols="12" sm="2">
                <v-autocomplete
                  v-model="selectedSupervisorId"
                  :items="filteredSupervisors"
                  item-text="name"
                  item-value="id"
                  :loading="supervisorsLoading"
                  clearable
                  outlined
                  class="primary-input"
                  :label="$t('Supervisor')"
                  hide-details
                  append-icon="mdi-chevron-down"
                  no-data-text="Nenhum registro"
                  @change="onSupervisorChange"
                ></v-autocomplete>
              </v-col>

              <!-- Institution filter (multi-institution users only) -->
              <v-col v-if="isMultiInstitution" cols="12" sm="3">
                <v-autocomplete
                  v-model="selectedInstitutionIds"
                  :items="filteredInstitutionOptions"
                  :search-input.sync="institutionSearch"
                  @blur="institutionSearch = ''"
                  item-value="id"
                  item-text="name"
                  clearable
                  chips
                  small-chips
                  multiple
                  deletable-chips
                  outlined
                  class="primary-input"
                  :label="$t('Instituição')"
                  hide-details
                  append-icon="mdi-chevron-down"
                  no-data-text="Nenhum registro"
                  :menu-props="{ maxHeight: 350 }"
                  @change="onInstitutionChange"
                >
                  <template v-slot:prepend-item>
                    <v-list-item @click="toggleSelectAllInstitutions">
                      <v-list-item-action>
                        <v-icon :color="selectedInstitutionIds.length > 0 ? '#bbcf20' : ''">
                          {{ selectAllIcon }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Selecionar todas</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-1"></v-divider>
                  </template>
                  <template v-slot:selection="{ index }">
                    <span v-if="index === 0" style="font-size: 13px">
                      {{ selectedInstitutionIds.length }} selecionada(s)
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col class="d-flex align-center">
                <v-autocomplete
                  v-model="unidadeIds"
                  :items="unidades"
                  :search-input.sync="searchInput"
                  @blur="searchInput = ''"
                  item-value="id"
                  item-text="name"
                  clearable
                  chips
                  small-chips
                  multiple
                  deletable-chips
                  outlined
                  class="primary-input"
                  :label="$t('Unidade')"
                  hide-details
                  append-icon="mdi-chevron-down"
                  no-data-text="Nenhum registro"
                >
                  <template
                    v-slot:selection="{ attrs, index, item, select, selected }"
                  >
                    <v-chip
                      v-bind="attrs"
                      :input-value="selected"
                      close
                      color="#BBCF20"
                      text-color="#fff"
                      small
                      @click="select"
                      @click:close="removeUnidade(index)"
                    >
                      {{ item.name }}
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item }">
                    <span class="text-caption">
                      {{ item.name }}
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col class="d-flex align-center">
                <v-autocomplete
                  v-model="departmentIds"
                  :items="localDepartments"
                  :search-input.sync="searchDepartmentInput"
                  @blur="searchDepartmentInput = ''"
                  item-value="id"
                  item-text="name"
                  clearable
                  chips
                  small-chips
                  multiple
                  deletable-chips
                  outlined
                  class="primary-input"
                  :label="$t('Setor')"
                  hide-details
                  append-icon="mdi-chevron-down"
                  no-data-text="Nenhum registro"
                >
                  <template
                    v-slot:selection="{ attrs, index, item, select, selected }"
                  >
                    <v-chip
                      v-bind="attrs"
                      :input-value="selected"
                      close
                      color="#BBCF20"
                      text-color="#fff"
                      small
                      @click="select"
                      @click:close="removeDepartment(index)"
                    >
                      {{ item.name }}
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item }">
                    <span class="text-caption">
                      {{ item.name }} / {{ item.unidade.name }}
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col class="d-flex align-center">
                <v-btn depressed class="primary-button mr-0" @click="filtrate">
                  {{ $t('Filtrar') }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <div>
      <v-row class="ma-0" no-gutters>
        <v-col cols="12" sm="3">
          <Schedule
            ref="schedule"
            :unidadeIds="unidadeIds"
            :departmentIds="departmentIds"
            :institutionIds="activeInstitutionIds"
          />
        </v-col>

        <v-col cols="12" sm="3">
          <Resolvability
            ref="resolvability"
            :unidadeIds="unidadeIds"
            :departmentIds="departmentIds"
            :institutionIds="activeInstitutionIds"
          />
        </v-col>

        <v-col cols="12" sm="3">
          <ScheduleCorrective
            ref="corrective"
            :unidadeIds="unidadeIds"
            :departmentIds="departmentIds"
            :institutionIds="activeInstitutionIds"
          />
        </v-col>

        <v-col cols="12" sm="3">
          <Satisfaction
            ref="satisfaction"
            :unidadeIds="unidadeIds"
            :departmentIds="departmentIds"
            :institutionIds="activeInstitutionIds"
          />
        </v-col>

        <v-col>
          <EquipStopped
            ref="equip"
            :unidadeIds="unidadeIds"
            :departmentIds="departmentIds"
            :institutionIds="activeInstitutionIds"
          />
        </v-col>

        <v-col>
          <v-card class="pa-3" outlined style="border-radius: 6px">
            <div class="pa-2 mb-2 grey lighten-2" style="border-radius: 6px">
              <strong>{{ $t('Custos Mês Vigente') }}</strong>
            </div>
            <v-tabs color="secondary">
              <v-tab>{{ $t('Executado') }}</v-tab>
              <v-tab>{{ $t('Evitado') }}</v-tab>
              <v-tab-item>
                <ExecutedCosts
                  ref="executed"
                  :unidadeIds="unidadeIds"
                  :departmentIds="departmentIds"
                  :institutionIds="activeInstitutionIds"
                />
              </v-tab-item>
              <v-tab-item>
                <AvoidedCosts
                  ref="avoided"
                  :unidadeIds="unidadeIds"
                  :departmentIds="departmentIds"
                  :institutionIds="activeInstitutionIds"
                />
              </v-tab-item>
            </v-tabs>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import ApiService from '../../../services/ApiService'
import Resolvability from './components/resolvability.vue'
import Schedule from './components/schedule.vue'
import EquipStopped from './components/equip-stopped.vue'
import AvoidedCosts from './components/avoided-costs.vue'
import ExecutedCosts from './components/executed-costs.vue'
import ScheduleCorrective from './components/schedule-corrective.vue'
import Satisfaction from './components/satisfaction.vue'

export default {
  name: 'DashPage',

  components: {
    Resolvability,
    Schedule,
    EquipStopped,
    AvoidedCosts,
    ExecutedCosts,
    ScheduleCorrective,
    Satisfaction,
  },

  data() {
    return {
      searchDepartmentInput: '',
      searchInput2: '',
      searchInput: '',
      institutionSearch: '',
      unidadeIds: [],
      departmentIds: [],
      selectedInstitutionIds: [],
      selectedSupervisorId: null,
      selectedAdminUserId: null,
      adminUsers: [],
      adminUsersLoading: false,
      savingPreset: false,
      unidadeLoading: false,
      departmentLoading: false,
      departments: [],
      localDepartments: [],
      unidades: [],
      institutions: [],
      supervisors: [],
      supervisorsLoading: false,
      institutionLoading: false,
      institutionId: '',
      userType: null,
      mainType: null,
      isMultiInstitution: false,
      apiService: new ApiService(),
    }
  },

  computed: {
    isAdminOrMaster() {
      return this.mainType === 0 || this.mainType === 1
    },

    selectedAdminHasPreset() {
      if (!this.selectedAdminUserId) return false
      const user = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
      return user && user.hasPreset
    },

    filteredInstitutionOptions() {
      let filtered = this.institutions
      if (this.selectedAdminUserId) {
        const adminUser = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (adminUser && adminUser.institutionIds) {
          filtered = filtered.filter((i) => adminUser.institutionIds.includes(i.id))
        }
      }
      if (this.selectedSupervisorId) {
        const supervisor = this.supervisors.find((s) => s.id === this.selectedSupervisorId)
        if (supervisor && supervisor.institutionIds) {
          filtered = filtered.filter((i) => supervisor.institutionIds.includes(i.id))
        }
      }
      return filtered
    },

    filteredSupervisors() {
      if (!this.selectedAdminUserId) return this.supervisors
      const adminUser = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
      if (!adminUser || !adminUser.institutionIds) return this.supervisors
      return this.supervisors.filter((s) =>
        s.institutionIds && s.institutionIds.some((id) => adminUser.institutionIds.includes(id))
      )
    },

    allInstitutionsSelected() {
      return this.filteredInstitutionOptions.length > 0 && this.selectedInstitutionIds.length === this.filteredInstitutionOptions.length
    },

    someInstitutionsSelected() {
      return this.selectedInstitutionIds.length > 0 && !this.allInstitutionsSelected
    },

    selectAllIcon() {
      if (this.allInstitutionsSelected) return 'mdi-checkbox-marked'
      if (this.someInstitutionsSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },

    activeInstitutionIds() {
      if (this.isMultiInstitution && this.selectedInstitutionIds.length > 0) {
        return this.selectedInstitutionIds
      }
      return []
    },
  },

  created() {
    const institution = JSON.parse(localStorage.getItem('institution'))
    const user = JSON.parse(localStorage.getItem('user'))
    this.userType = institution.userType
    this.mainType = user.mainType
    this.institutionId = institution.id

    this.initDashboard()
  },

  methods: {
    async initDashboard() {
      if (this.isAdminOrMaster) {
        this.isMultiInstitution = true
        await this.getInstitutions()
      } else {
        const userInstitutions = await this.getUserInstitutions()
        if (userInstitutions && userInstitutions.length > 1) {
          this.isMultiInstitution = true
          this.institutions = userInstitutions
        }
      }

      if (this.userType > 1) {
        this.getUserUnidades()
      } else {
        this.getUnidades()
      }

      this.getDepartments()
    },

    async getInstitutions() {
      this.institutionLoading = true
      try {
        const r = await this.apiService.get('institution/list')
        this.institutions = r.content
        if (this.isAdminOrMaster) {
          this.getSupervisors()
          this.getAdminUsers()
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.institutionLoading = false
      }
    },

    async getUserInstitutions() {
      try {
        const r = await this.apiService.get('institution/ListUser')
        return r.content || []
      } catch (e) {
        console.error(e)
        return []
      }
    },

    async getSupervisors() {
      this.supervisorsLoading = true
      try {
        const allIds = this.institutions.map((i) => i.id)
        const r = await this.apiService.post('bigData/users', allIds)
        this.supervisors = r.content || []
      } catch (e) {
        console.error(e)
      } finally {
        this.supervisorsLoading = false
      }
    },

    async getAdminUsers() {
      this.adminUsersLoading = true
      try {
        const allIds = this.institutions.map((i) => i.id)
        const r = await this.apiService.post('bigData/admin-users', allIds)
        this.adminUsers = (r.content || []).map((u) => ({
          ...u,
          displayName: u.name + ' (' + u.role + ')',
        }))
      } catch (e) {
        console.error(e)
      } finally {
        this.adminUsersLoading = false
      }
    },

    onAdminUserChange() {
      this.selectedSupervisorId = null
      this.unidadeIds = []
      this.departmentIds = []
      this.unidades = []
      this.localDepartments = []
      if (this.selectedAdminUserId) {
        const adminUser = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (adminUser && adminUser.hasPreset && adminUser.presetInstitutionIds) {
          this.selectedInstitutionIds = [...adminUser.presetInstitutionIds]
          this.onInstitutionChange()
          this.filtrate()
          return
        } else {
          this.selectedInstitutionIds = adminUser ? [...adminUser.institutionIds] : []
        }
      } else {
        this.selectedInstitutionIds = []
      }
      this.onInstitutionChange()
    },

    onSupervisorChange() {
      this.unidadeIds = []
      this.departmentIds = []
      this.unidades = []
      this.localDepartments = []
      if (this.selectedSupervisorId) {
        const supervisor = this.supervisors.find((s) => s.id === this.selectedSupervisorId)
        this.selectedInstitutionIds = supervisor ? [...supervisor.institutionIds] : []
      } else if (this.selectedAdminUserId) {
        const adminUser = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (adminUser && adminUser.hasPreset && adminUser.presetInstitutionIds) {
          this.selectedInstitutionIds = [...adminUser.presetInstitutionIds]
        } else {
          this.selectedInstitutionIds = adminUser ? [...adminUser.institutionIds] : []
        }
      } else {
        this.selectedInstitutionIds = []
      }
      this.onInstitutionChange()
    },

    async savePreset() {
      if (!this.selectedAdminUserId || !this.selectedInstitutionIds.length) return
      this.savingPreset = true
      try {
        await this.apiService.post('dashboardFilterPreset', {
          userId: this.selectedAdminUserId,
          institutionIds: this.selectedInstitutionIds,
        })
        const user = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (user) {
          user.presetInstitutionIds = [...this.selectedInstitutionIds]
          user.hasPreset = true
        }
        this.$toast.success('Preset salvo com sucesso!')
      } catch (e) {
        console.error(e)
        this.$toast.error('Erro ao salvar preset')
      } finally {
        this.savingPreset = false
      }
    },

    async removePreset() {
      if (!this.selectedAdminUserId) return
      this.savingPreset = true
      try {
        await this.apiService.delete('dashboardFilterPreset/' + this.selectedAdminUserId)
        const user = this.adminUsers.find((a) => a.id === this.selectedAdminUserId)
        if (user) {
          user.presetInstitutionIds = null
          user.hasPreset = false
        }
        this.$toast.success('Preset removido com sucesso!')
      } catch (e) {
        console.error(e)
        this.$toast.error('Erro ao remover preset')
      } finally {
        this.savingPreset = false
      }
    },

    onInstitutionChange() {
      this.unidadeIds = []
      this.departmentIds = []
      if (this.selectedInstitutionIds.length > 0) {
        this.getUnidadesByInstitutions()
        this.getDepartmentsByInstitutions()
      } else {
        if (this.userType > 1) {
          this.getUserUnidades()
        } else {
          this.getUnidades()
        }
        this.getDepartments()
      }
    },

    toggleSelectAllInstitutions() {
      if (this.allInstitutionsSelected) {
        this.selectedInstitutionIds = []
      } else {
        this.selectedInstitutionIds = this.filteredInstitutionOptions.map((i) => i.id)
      }
      this.onInstitutionChange()
    },

    filtrate() {
      if (this.$refs.resolvability) this.$refs.resolvability.list()
      if (this.$refs.schedule) this.$refs.schedule.list()
      if (this.$refs.equip) this.$refs.equip.list()
      if (this.$refs.avoided) this.$refs.avoided.list()
      if (this.$refs.executed) this.$refs.executed.list()
      if (this.$refs.corrective) this.$refs.corrective.list()
      if (this.$refs.satisfaction) this.$refs.satisfaction.list()
    },

    async getUnidades() {
      this.unidadeLoading = true

      await this.apiService
        .get(`unidade/list/${this.institutionId}`)
        .then((response) => {
          this.unidades = response.content
          this.unidadeLoading = false
        })
        .catch(() => {
          this.unidadeLoading = false
        })
    },

    async getUserUnidades() {
      this.unidadeLoading = true

      await this.apiService
        .get(`unidade/listByUserAndInstitution/${this.institutionId}`)
        .then((response) => {
          this.unidades = response.content
          this.unidadeLoading = false
        })
    },

    async getUnidadesByInstitutions() {
      this.unidadeLoading = true
      try {
        const r = await this.apiService.post('unidade/listByIntitutionIds', { ids: this.selectedInstitutionIds })
        this.unidades = r.content
      } catch (e) {
        console.error(e)
      } finally {
        this.unidadeLoading = false
      }
    },

    async getDepartments() {
      this.departmentLoading = true
      await this.apiService
        .get(`department/listActive/${this.institutionId}`)
        .then((response) => {
          this.departments = response.content
          this.localDepartments = response.content
          this.departmentLoading = false
        })
        .catch(() => {
          this.departmentLoading = false
        })
    },

    async getDepartmentsByInstitutions() {
      this.departmentLoading = true
      try {
        const allDepts = []
        for (const instId of this.selectedInstitutionIds) {
          const r = await this.apiService.get(`department/listActive/${instId}`)
          if (r.content) allDepts.push(...r.content)
        }
        this.departments = allDepts
        this.localDepartments = allDepts
      } catch (e) {
        console.error(e)
      } finally {
        this.departmentLoading = false
      }
    },

    removeUnidade(index) {
      this.unidadeIds.splice(index, 1)
    },

    removeDepartment(index) {
      this.departmentIds.splice(index, 1)
    },
  },

  watch: {
    unidadeIds() {
      if (this.unidadeIds && this.unidadeIds.length > 0) {
        this.localDepartments = this.departments.filter((x) =>
          this.unidadeIds.includes(x.unidadeId)
        )
      } else {
        this.localDepartments = this.departments
      }
    },
  },
}
</script>

<style scoped></style>
